import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import GameShell from "../../components/Kids/GameShell";
import StatCard from "../../components/Kids/StatCard";
import ProgressBar from "../../components/Kids/ProgressBar";
import EmptyState from "../../components/Kids/EmptyState";
import Skeleton from "../../components/Kids/Skeleton";

const API   = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem("kids_parent_token");

const TROPHY_BG = "linear-gradient(160deg, #FFF8F2 0%, #FFE9D6 45%, #FFD9BF 100%)";

export default function KidsAchievements() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const childId  = params.get("childId") || sessionStorage.getItem("childId");

  const [badges, setBadges]   = useState([]);
  const [summary, setSummary] = useState({ total_xp: 0, level: 1, earned_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  const loadAchievements = useCallback(() => {
    if (!childId) return;
    setLoading(true);
    setError(false);
    axios.get(`${API}/api/kids/achievements/${childId}`, {
      headers: { Authorization: `Bearer ${token()}` },
    })
      .then(r => {
        const d = r.data?.data;
        setBadges(d?.badges || d?.achievements || []);
        setSummary({
          total_xp:     d?.total_xp ?? 0,
          level:        d?.level ?? 1,
          earned_count: d?.earned_count ?? (d?.badges || d?.achievements || []).filter(b => b.earned).length,
        });
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [childId]);

  useEffect(() => { loadAchievements(); }, [loadAchievements]);

  const earned = badges.filter(b => b.earned);
  const locked = badges.filter(b => !b.earned);

  return (
    <GameShell
      title={t("kids.achievements.title")}
      emoji="🏅"
      theme="light"
      bg={TROPHY_BG}
      onBack={() => navigate(`/kids/games?childId=${childId}`)}
      maxWidth={760}
      showControls
    >
      <style>{`
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        .float{animation:float 3s ease-in-out infinite}
      `}</style>

      {loading ? (
        <TrophySkeleton />
      ) : error ? (
        <EmptyState
          mood="wrong"
          theme="light"
          title="Oops — no internet!"
          subtitle="We couldn't load your trophies. Check your connection and try again."
          cta="Retry"
          onCta={loadAchievements}
        />
      ) : (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-3 gap-3 mt-4 mb-6">
            <StatCard emoji="⭐" value={summary.total_xp} label="Total XP" />
            <StatCard emoji="🎖️" value={summary.level} label="Level" />
            <StatCard emoji="🏅" value={summary.earned_count} label="Badges" />
          </div>

          {/* Earned badges */}
          {earned.length > 0 && (
            <>
              <h2 className="text-ink font-display font-semibold text-sm mb-3 flex items-center gap-2">
                ✨ {t("kids.achievements.earnedBadges")} ({earned.length})
              </h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-6">
                {earned.map((badge, i) => (
                  <BadgeCard key={badge.id || i} badge={badge} earned />
                ))}
              </div>
            </>
          )}

          {/* Locked badges */}
          {locked.length > 0 && (
            <>
              <h2 className="text-faint font-display font-semibold text-sm mb-3 flex items-center gap-2">
                🔒 {t("kids.achievements.locked")} ({locked.length})
              </h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {locked.map((badge, i) => (
                  <BadgeCard key={badge.id || i} badge={badge} earned={false} />
                ))}
              </div>
            </>
          )}

          {badges.length === 0 && (
            <EmptyState
              mood="thinking"
              theme="light"
              emoji="🏅"
              title={t("kids.achievements.noBadges")}
              subtitle={t("kids.achievements.noBadgesDesc")}
              cta={t("kids.achievements.playAGame")}
              onCta={() => navigate(`/kids/games?childId=${childId}`)}
            />
          )}
        </>
      )}
    </GameShell>
  );
}

/* ── Loading skeleton: stat tiles + trophy grid shaped like the real layout ── */
function TrophySkeleton() {
  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mt-4 mb-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" rounded="16px" />
        ))}
      </div>
      <Skeleton className="h-4 w-32 mb-3" rounded="8px" />
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-[132px] w-full" rounded="16px" />
        ))}
      </div>
    </div>
  );
}

function BadgeCard({ badge, earned }) {
  const progress = badge.progress ?? 0;

  return (
    <div className={`relative rounded-card p-3 text-center shadow-card transition ${
      earned
        ? "bg-white border-2 border-gold"
        : "bg-cream/60 border border-cardline"
    }`}>
      {/* Earned tag */}
      {earned && (
        <div className="absolute -top-2 -end-2 bg-success text-white text-[9px] font-body font-extrabold px-2 py-0.5 rounded-full shadow-card">
          ✓ Earned
        </div>
      )}

      {/* Badge emoji */}
      <div className={`text-4xl mb-2 ${earned ? "" : "grayscale opacity-50"}`}>
        {badge.emoji || "🏅"}
      </div>

      {/* Name */}
      <h3 className={`font-display font-semibold text-sm mb-1 ${earned ? "text-ink" : "text-faint"}`}>
        {badge.name || "Badge"}
      </h3>

      {/* Description */}
      <p className={`text-[11px] mb-2 leading-tight font-body ${earned ? "text-muted" : "text-faint"}`}>
        {badge.description || ""}
      </p>

      {/* Progress bar */}
      {!earned && (
        <div>
          <div className="rounded-full p-[2px]" style={{ background: "linear-gradient(90deg,#FBB03B,#EE5F2B)" }}>
            <ProgressBar value={Math.min(progress, 100) / 100} className="h-1.5" />
          </div>
          <p className="text-[10px] text-faint mt-1 font-body font-semibold">{progress}%</p>
        </div>
      )}
    </div>
  );
}
